<template>
  <div class="flex-1 px-6 py-6 overflow-y-auto">
    <div class="pb-4 border-b border-gray-300">
      <h1 class="text-2xl font-semibold text-gray-900">เปลี่ยนรหัสผ่านใหม่</h1>
    </div>

    <!-- Error Message -->
    <div
      v-if="errors.length > 0"
      class="px-4 py-3 rounded-md bg-red-600 bg-opacity-20 flex items-center"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-5 w-5 text-red-600"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clip-rule="evenodd"
        />
      </svg>
      <p class="text-base font-semibold text-red-800 flex-1 px-3">
        {{ errors[0] }}
      </p>
      <button @click="errors = []" type="button" class="focus:outline-none">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-red-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>
    <!-- End Error Message -->

    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
      <div class="divide-y divide-gray-300">
        <div class="py-4 grid grid-cols-4">
          <label class="text-base font-medium text-gray-700"
            >รหัสผ่านเดิม<span class="text-xl text-red-600">*</span>
          </label>
          <div class="col-span-2 max-w-lg">
            <ValidationProvider
              name="รหัสผ่านเดิม"
              rules="required"
              v-slot="{ errors }"
            >
              <input
                v-model="oldPassword"
                type="password"
                class="
                  leading-none
                  focus:ring-cyan-500 focus:border-cyan-500
                  block
                  w-full
                  shadow-sm
                  border-gray-300
                  rounded-md
                "
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>

        <div class="py-4 grid grid-cols-4">
          <label class="text-base font-medium text-gray-700"
            >รหัสผ่านใหม่<span class="text-xl text-red-600">*</span>
          </label>
          <div class="col-span-2 max-w-lg">
            <ValidationProvider
              name="รหัสผ่านใหม่"
              vid="confirmation"
              rules="required|min:6|max:128"
              v-slot="{ errors }"
            >
              <input
                v-model="newPassword"
                type="password"
                class="
                  leading-none
                  focus:ring-cyan-500 focus:border-cyan-500
                  block
                  w-full
                  shadow-sm
                  border-gray-300
                  rounded-md
                "
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>

        <div class="py-4 grid grid-cols-4">
          <label class="text-base font-medium text-gray-700"
            >ยืนยันรหัสผ่าน<span class="text-xl text-red-600">*</span>
          </label>
          <div class="col-span-2 max-w-lg">
            <ValidationProvider
              name="ยืนยันรหัสผ่าน"
              rules="required|confirmed:confirmation"
              v-slot="{ errors }"
            >
              <input
                v-model="confirmPassword"
                type="password"
                class="
                  leading-none
                  focus:ring-cyan-500 focus:border-cyan-500
                  block
                  w-full
                  shadow-sm
                  border-gray-300
                  rounded-md
                "
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>

        <div class="py-4 grid grid-cols-4">
          <div class="col-start-2 col-span-2">
            <button
              @click="handleSubmit(onSave)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              เปลี่ยนรหัสผ่าน
            </button>
            <button
              @click="onCancel"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-gray-300
                shadow-sm
                px-4
                py-2
                bg-white
                text-base
                font-medium
                text-gray-700
                hover:bg-gray-50
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:ml-3 sm:w-auto
              "
            >
              ยกเลิก
            </button>
          </div>
        </div>
      </div>
    </ValidationObserver>

    <!-- confirm leave page -->
    <AppConfirmLeavePageModal
      :show="isConfirmLeavePageShow"
      :next="isConfirmLeavePageNextFunction"
      @cancel="onCancelConfirmLeavePage"
      @submit="onSubmitConfirmLeavePage"
    />
    <!-- end confirm leave page -->
  </div>
</template>

<script>
import AppConfirmLeavePageModal from '@/components/AppConfirmLeavePageModal'

import { extend } from 'vee-validate'
import { required, min, max, confirmed } from 'vee-validate/dist/rules'

// No message specified.
// extend('required', required)

// Override the default message.
extend('required', {
  ...required,
  message: 'จำเป็นต้องระบุ {_field_}',
})
extend('min', {
  ...min,
  params: ['length'],
  message: '{_field_} ต้องมากกว่า {length} ตัวอักษร',
})
extend('max', {
  ...max,
  params: ['length'],
  message: '{_field_} ต้องน้อยกว่า {length} ตัวอักษร',
})
extend('confirmed', {
  ...confirmed,
  message: '{_field_} ไม่ถูกต้อง',
})

export default {
  name: 'ChangePassword',

  components: {
    AppConfirmLeavePageModal,
  },

  data() {
    return {
      loading: false,
      errors: [],
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      timeout: null,
      isConfirmLeavePageShow: false,
      isConfirmLeavePageNextFunction: null,
    }
  },

  computed: {
    isDataChange() {
      if (
        this.oldPassword !== '' ||
        this.newPassword !== '' ||
        this.confirmPassword !== ''
      ) {
        return true
      }
      return false
    },
  },

  created() {
    this.$store.commit('setMenusCustomerActive', false)
    this.$store.commit('setBreadcrumbs', [
      { text: 'เปลี่ยนรหัสผ่าน', routeName: 'ChangePassword' },
    ])
  },

  methods: {
    onCancelConfirmLeavePage() {
      this.isConfirmLeavePageShow = false
    },

    onSubmitConfirmLeavePage(obj) {
      this.isConfirmLeavePageShow = false
      this.timeout = setTimeout(() => {
        obj.next()
      }, 200)
      return
    },

    onCancel() {
      this.oldPassword = ''
      this.newPassword = ''
      this.confirmPassword = ''

      this.$refs.observer.reset()
    },

    async onSave() {
      try {
        const { taff_admin_uuid } = this.$store.state.auth.currentUser
        const credential = {
          taff_admin_uuid,
          password: this.oldPassword,
          password_new: this.newPassword,
        }

        this.loading = true

        await this.$store.dispatch('auth/changePassword', credential)

        this.$store
          .dispatch('auth/logout')
          .then(() => this.$router.push({ name: 'Login' }))
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong.'
          )
        }

        console.log(error.response)
      } finally {
        this.loading = false

        this.oldPassword = ''
        this.newPassword = ''
        this.confirmPassword = ''
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    if (this.isDataChange) {
      this.isConfirmLeavePageShow = true
      this.isConfirmLeavePageNextFunction = next
      return
    }

    next()
  },
}
</script>
